/**
 * Auth state — unified for guests, staff, and owners.
 */

import { createEffect, createSignal } from 'solid-js'
import { demoLogin, getGuestProfile, loginStaff, requestOtp, verifyOtp } from './api'
import { markDemoSession } from './demo'

export type UserRole = 'guest' | 'staff' | 'manager' | 'owner'

export interface UserVenue {
  venueId: string
  venueName: string
  role: UserRole
}

export interface User {
  id: string
  email: string
  balance: number
  itemsCheckedIn: number
  referralCode: string
  venues: UserVenue[]
}

// A demo session must not survive an app restart — it exists to be clicked through
// from the start page. This runs ONCE, at module load. It must NOT live in the
// session-restore effect below: that effect also fires whenever a token is set, so it
// wiped the demo session the instant someone signed in and the guest bounced straight
// back to /signup. That is why the demo guest appeared completely broken.
if (typeof localStorage !== 'undefined' && localStorage.getItem('session_demo') === '1') {
  localStorage.removeItem('session_token')
  localStorage.removeItem('session_kind')
  localStorage.removeItem('session_staff')
  localStorage.removeItem('session_demo')
}

const [token, setToken] = createSignal<string | null>(localStorage.getItem('session_token'))
const [user, setUser] = createSignal<User | null>(null)
const [activeVenue, setActiveVenue] = createSignal<UserVenue | null>(null)
const [mode, setMode] = createSignal<'guest' | 'staff'>('guest')
const [isLoading, setIsLoading] = createSignal(false)
const [error, setError] = createSignal<string | null>(null)

// Restore session on load.
// `/v1/guests/me` is guest-only: calling it with a staff token failed, the catch
// below wiped the token, and a staff session died on every reload. Remember the
// session kind and rebuild a staff session from the stored payload instead.
createEffect(async () => {
  if (token()) {
    if (localStorage.getItem('session_kind') === 'staff') {
      const raw = localStorage.getItem('session_staff')
      if (raw) {
        try {
          const staff = JSON.parse(raw)
          setMode('staff')
          const venue = { venueId: staff.venueId, venueName: staff.venueName ?? 'Venue', role: staff.role as UserRole }
          setActiveVenue(venue)
          setUser({
            id: staff.id,
            email: staff.email,
            balance: 0,
            itemsCheckedIn: 0,
            referralCode: '',
            venues: [venue],
          })
        } catch {
          localStorage.removeItem('session_token')
          localStorage.removeItem('session_staff')
          localStorage.removeItem('session_kind')
          setToken(null)
        }
      }
      return
    }
    try {
      const res = await getGuestProfile()
      if (res.data) {
        const u = res.data
        setUser({
          id: u.id,
          email: u.email,
          balance: u.balance,
          itemsCheckedIn: u.itemsCheckedIn,
          referralCode: u.referralCode,
          venues: u.roles || [],
        })
        // Auto-select first venue if user has staff roles
        if (u.roles?.length > 0) {
          setActiveVenue(u.roles[0])
        }
      } else {
        localStorage.removeItem('session_token')
        setToken(null)
      }
    } catch (err) {
      console.error('Auth restore failed:', err)
      localStorage.removeItem('session_token')
      setToken(null)
    }
  }
})

export function useAuth() {
  return {
    token,
    user,
    activeVenue,
    mode,
    isLoading,
    error,
    isAuthenticated: () => !!token() && !!user(),
    hasStaffRole: () => (user()?.venues?.length ?? 0) > 0,
    isOwner: () => activeVenue()?.role === 'owner',
    isManager: () => activeVenue()?.role === 'manager' || activeVenue()?.role === 'owner',
    isStaff: () => activeVenue()?.role === 'staff',

    async sendOtp(email: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      // try/finally: a thrown fetch (offline, blocked request, DNS) used to escape
      // this function leaving isLoading true forever — the button sat on "Sending..."
      // with no way forward. Every exit path must clear the flag.
      try {
        const res = await requestOtp(email)
        if (res.error) {
          setError(res.error.message)
          return false
        }
        // The API tells us whether the email actually left the building.
        if (res.data && (res.data as any).emailSent === false) {
          setError('We could not send the code to that address. Please try again in a moment.')
          return false
        }
        return true
      } catch (e) {
        setError('Could not reach ZipCheck — check your connection and try again.')
        return false
      } finally {
        setIsLoading(false)
      }
    },

    async verifyOtp(email: string, code: string, name?: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      try {
        const res = await verifyOtp(email, code, name)
        if (res.error || !res.data) {
          setError(res.error?.message || 'Verification failed')
          return false
        }
        localStorage.setItem('session_token', res.data.token)
        localStorage.setItem('session_kind', 'guest')
        localStorage.removeItem('session_staff')
        setToken(res.data.token)
        await this.refreshProfile()
        return true
      } catch (e) {
        setError('Could not verify the code — check your connection and try again.')
        return false
      } finally {
        setIsLoading(false)
      }
    },

    /**
     * A staff session has no guest profile, so `/v1/guests/me` cannot populate
     * the user — that used to leave isAuthenticated() false and bounce staff
     * straight back to signup. Build the user from the login response instead.
     */
    applyStaffSession(
      token: string,
      staff: { id: string; email: string; role: string; venueId: string; venueName: string | null },
    ) {
      localStorage.setItem('session_token', token)
      localStorage.removeItem('session_demo')
      localStorage.setItem('session_kind', 'staff')
      localStorage.setItem('session_staff', JSON.stringify(staff))
      setToken(token)
      setMode('staff')
      const venue = { venueId: staff.venueId, venueName: staff.venueName ?? 'Venue', role: staff.role as UserRole }
      setActiveVenue(venue)
      setUser({
        id: staff.id,
        email: staff.email,
        balance: 0,
        itemsCheckedIn: 0,
        referralCode: '',
        venues: [venue],
      })
    },

    async loginAsStaff(email: string, password: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      try {
        const res = await loginStaff(email, password)
        if (res.error || !res.data) {
          setError(res.error?.message || 'Login failed')
          return false
        }
        this.applyStaffSession(res.data.token, res.data.staff)
        return true
      } catch (e) {
        setError('Could not sign in — check your connection and try again.')
        return false
      } finally {
        setIsLoading(false)
      }
    },

    /**
     * Sign in the seeded demo account on demo.zipcheck.my — no OTP, no password.
     *
     * The lane is inert on production (the Worker answers 404 unless DEMO_MODE is
     * on), so this method is shared code that simply cannot fire there. It reuses
     * applyStaffSession for the staff half so the demo staff session behaves
     * identically to a real one — no demo-only branching in the components.
     */
    async signInDemo(email: string): Promise<boolean> {
      setIsLoading(true)
      setError(null)
      try {
        const res = await demoLogin(email)
        if (res.error || !res.data) {
          setError(res.error?.message || 'Demo sign-in failed')
          return false
        }
        const d = res.data
        // Mark the session as demo BEFORE storing the token: auth.ts clears a
        // lingering demo marker at module load, so this survives the reload that
        // follows and keeps the demo identifiable.
        markDemoSession()
        if (d.staff) {
          this.applyStaffSession(d.token, d.staff)
          return true
        }
        localStorage.setItem('session_token', d.token)
        localStorage.setItem('session_kind', 'guest')
        localStorage.removeItem('session_staff')
        markDemoSession()
        setToken(d.token)
        await this.refreshProfile()
        return true
      } catch (e) {
        setError('Could not reach the demo — check your connection and try again.')
        return false
      } finally {
        setIsLoading(false)
      }
    },

    async refreshProfile() {
      const res = await getGuestProfile()
      if (res.data) {
        const u = res.data
        setUser({
          id: u.id,
          email: u.email,
          balance: u.balance,
          itemsCheckedIn: u.itemsCheckedIn,
          referralCode: u.referralCode,
          venues: u.roles || [],
        })
      }
    },

    switchVenue(venue: UserVenue) {
      setActiveVenue(venue)
    },

    switchMode(newMode: 'guest' | 'staff') {
      setMode(newMode)
    },

    logout() {
      localStorage.removeItem('session_token')
      localStorage.removeItem('session_kind')
      localStorage.removeItem('session_staff')
      localStorage.removeItem('session_demo')
      setToken(null)
      setUser(null)
      setActiveVenue(null)
      setMode('guest')
    },
  }
}
